import { useMemo, useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Select,
  Text,
} from '@chakra-ui/react';
import { Currency } from 'components/Number';
import PropTypes from 'prop-types';

import { cohortRepaymentSchedule, monthCohortContributions, twelveNetRows } from './cashflowFeeExposureExport';
import { formatAccountingCurrency, formatMonthLabel } from './cashflowFeeExposureHelpers';

function formatRoas(value) {
  if (value == null) return '—';
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value)}%`;
}

function moneyCell(amount) {
  return amount ? <Currency value={amount} maximumFractionDigits={0} /> : '—';
}

function netCell(amount) {
  if (!amount) return '—';
  return formatAccountingCurrency(amount);
}

function CohortRepaymentSection({ customer, customers, months, showCohortRepayments = true }) {
  const [showRoas, setShowRoas] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState('');

  const rows = useMemo(
    () => (showCohortRepayments ? cohortRepaymentSchedule(customer) : []),
    [customer, showCohortRepayments]
  );
  const periodCount = rows.reduce((max, row) => Math.max(max, row.amounts.length), 0);
  const calendarMonths = useMemo(() => {
    const source = months || customer?.months || [];
    return [...source].map((month) => month.month).filter(Boolean).sort();
  }, [months, customer]);
  const selectedMonth = calendarMonths.includes(calendarMonth)
    ? calendarMonth
    : calendarMonths[calendarMonths.length - 1] || '';
  const contributions = useMemo(
    () => monthCohortContributions(customer, selectedMonth),
    [customer, selectedMonth]
  );
  const feeTotal = contributions.reduce((sum, row) => sum + row.fee, 0);
  const repaymentTotal = contributions.reduce((sum, row) => sum + row.repayment, 0);
  const beginningTotal = contributions.reduce((sum, row) => sum + row.beginning, 0);
  const endingTotal = contributions.reduce((sum, row) => sum + row.ending, 0);
  const net = useMemo(
    () => (showCohortRepayments ? null : twelveNetRows(customers, months || customer?.months)),
    [showCohortRepayments, customers, months, customer]
  );

  return (
    <Box mt={8}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={3}>
        <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em">
          Fees, Repayment and Exposure
        </Text>
        <Flex align="center" gap={3} wrap="wrap">
          <Select
            size="sm"
            w="180px"
            aria-label="Calendar month"
            value={selectedMonth}
            onChange={(event) => setCalendarMonth(event.target.value)}
            isDisabled={!calendarMonths.length}
          >
            {calendarMonths.map((month) => (
              <option key={month} value={month}>{formatMonthLabel(month)}</option>
            ))}
          </Select>
        </Flex>
      </Flex>
      <Text fontSize="sm" color="gray.600" mb={3}>
        {showCohortRepayments
          ? 'The customer selected on the chart and the month apply here. Each row is how much of that month’s beginning exposure, fee, repayment, and ending exposure belongs to one cohort.'
          : 'All customers on the chart. Each row is how much of that month’s beginning exposure, fee, repayment, and ending exposure belongs to one cohort.'}
      </Text>
      {!contributions.length ? (
        <Text color="gray.500">This month has no exposure, fee, or repayment split by cohort.</Text>
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
            <thead>
              <tr>
                {['Cohort', 'Beginning exposure', 'Fee', 'Repayment', 'Ending exposure'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: label === 'Cohort' ? 'left' : 'right',
                      padding: '8px 12px',
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contributions.map((row) => (
                <tr key={row.key}>
                  <td style={{ padding: '8px 12px' }}>{row.label}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{moneyCell(row.beginning)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{moneyCell(row.fee)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{moneyCell(row.repayment)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{moneyCell(row.ending)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: '8px 12px', fontWeight: 700, borderTop: '2px solid #1F4E79' }}>Total</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #1F4E79' }}>{moneyCell(beginningTotal)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #1F4E79' }}>{moneyCell(feeTotal)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #1F4E79' }}>{moneyCell(repaymentTotal)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #1F4E79' }}>{moneyCell(endingTotal)}</td>
              </tr>
            </tbody>
          </Box>
        </Box>
      )}

      <Box mt={8}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={3}>
          <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em">
            {showCohortRepayments ? 'COHORT REPAYMENTS' : 'Net for Twelve'}
          </Text>
          {showCohortRepayments && (
            <Flex align="center" gap={3} wrap="wrap">
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button
                  colorScheme={!showRoas ? 'brand' : undefined}
                  variant={!showRoas ? 'solid' : 'outline'}
                  onClick={() => setShowRoas(false)}
                >
                  Amount
                </Button>
                <Button
                  colorScheme={showRoas ? 'brand' : undefined}
                  variant={showRoas ? 'solid' : 'outline'}
                  onClick={() => setShowRoas(true)}
                >
                  ROAS %
                </Button>
              </ButtonGroup>
            </Flex>
          )}
        </Flex>
        {showCohortRepayments && (
          <Text fontSize="sm" color="gray.600" mb={3}>
            Every cohort for this customer. The month above does not change this table.
          </Text>
        )}
        {!showCohortRepayments ? (
          <TwelveNetTable net={net} />
        ) : !rows.length ? (
          <Text color="gray.500">This customer has no cohort repayments yet.</Text>
        ) : (
          <Box overflowX="auto">
            <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
              <thead>
                <tr>
                  {['Cohort month', 'Funded month', 'Amount funded', ...Array.from({ length: periodCount }, (_, index) => String(index + 1))].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: label === 'Cohort month' ? 'left' : 'right',
                        padding: '8px 12px',
                        position: label === 'Cohort month' ? 'sticky' : undefined,
                        left: label === 'Cohort month' ? 0 : undefined,
                        background: '#fff',
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td style={{ padding: '8px 12px', position: 'sticky', left: 0, background: '#fff' }}>
                      {row.cohortMonth ? formatMonthLabel(row.cohortMonth) : row.label}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {row.fundingMonth ? formatMonthLabel(row.fundingMonth) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {row.funding ? <Currency value={row.funding} maximumFractionDigits={0} /> : '—'}
                    </td>
                    {Array.from({ length: periodCount }, (_, index) => {
                      const amount = row.amounts[index];
                      const roas = row.roasPct[index];
                      return (
                        <td key={index} style={{ padding: '8px 12px', textAlign: 'right' }}>
                          {showRoas
                            ? formatRoas(roas)
                            : amount
                              ? <Currency value={amount} maximumFractionDigits={0} />
                              : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TwelveNetTable({ net }) {
  if (!net?.rows.length || !net.monthKeys.length) {
    return <Text color="gray.500">No customers to summarize.</Text>;
  }
  const headerCell = {
    padding: '8px 12px',
    background: '#fff',
  };
  return (
    <>
      <Text fontSize="sm" color="gray.600" mb={3}>
        Each cell is repayment minus growth for that customer and month.
      </Text>
      <Box overflowX="auto">
        <Box as="table" w="max-content" minW="100%" fontFamily="body" fontSize="sm">
          <thead>
            <tr>
              <th style={{ ...headerCell, textAlign: 'left', position: 'sticky', left: 0 }}>Customer</th>
              {net.monthKeys.map((month) => (
                <th key={month} style={{ ...headerCell, textAlign: 'right' }}>{formatMonthLabel(month)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {net.rows.map((row) => (
              <tr key={row.customerId || row.name}>
                <td style={{ padding: '8px 12px', position: 'sticky', left: 0, background: '#fff' }}>{row.name}</td>
                {row.amounts.map((amount, index) => (
                  <td key={net.monthKeys[index]} style={{ padding: '8px 12px', textAlign: 'right' }}>{netCell(amount)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ padding: '8px 12px', fontWeight: 700, position: 'sticky', left: 0, background: '#fff', borderTop: '2px solid #1F4E79' }}>Total</td>
              {net.totals.map((amount, index) => (
                <td
                  key={net.monthKeys[index]}
                  style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, borderTop: '2px solid #1F4E79' }}
                >
                  {netCell(amount)}
                </td>
              ))}
            </tr>
          </tbody>
        </Box>
      </Box>
    </>
  );
}

TwelveNetTable.propTypes = {
  net: PropTypes.shape({
    monthKeys: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.object),
    totals: PropTypes.arrayOf(PropTypes.number),
  }),
};

CohortRepaymentSection.propTypes = {
  customer: PropTypes.shape({
    months: PropTypes.arrayOf(PropTypes.object),
  }),
  customers: PropTypes.arrayOf(PropTypes.object),
  months: PropTypes.arrayOf(PropTypes.object),
  showCohortRepayments: PropTypes.bool,
};

export default CohortRepaymentSection;
